// buildSessionUser
// -----------------
// Pure assembly of the App `user` object from the rows fetched during
// session-restore / login (auth user + profiles + user_shelters + shelters +
// delivery_addresses). Extracted VERBATIM out of portal.jsx so the derivation --
// including the shelterUuid mapping that caused the "shelter_id: null on every
// post-refresh submit" bug -- is unit-tested in isolation. The async DB fetches
// stay in portal.jsx; this function only does the (pure) shaping.
//
// TypeScript source -- sessionUser.js is a thin re-export stub.

import { defaultScreenFor } from "../app/access";
import { listActiveByShelterId } from "../data/deliveryAddresses";
import { getProfileByIdResult } from "../data/profiles";
import { getShelterById } from "../data/shelters";
import { listUserSheltersByUserId } from "../data/userShelters";
import { logAudit } from "./auditLog";
import { ROLES } from "./enums";
import { logger } from "./logger";
import { normaliseLoginMethod } from "./loginMethod";

export interface SessionAuthUser {
  id: string;
  email?: string;
}

export interface SessionProfileRow {
  id: string;
  role?: string | null;
  display_name?: string | null;
  // "Display Name" in the staff editor — the SHORT public name a Zephyr STAFF member is shown as
  // on shelter-facing surfaces (e.g. "John W (Zephyr)"), so staff full names don't leak to
  // shelters. Staff only; NULL on shelter rows. See attributedName().
  preferred_name?: string | null;
  // Contact-model person name (staff_* replaced profiles.first_name/last_name in
  // Phase 3). Carried onto the session user's firstName/lastName so the restore path
  // matches fresh login — a request's Contact Name reads these (see buildSessionUser).
  staff_first_name?: string | null;
  staff_last_name?: string | null;
  shelter_code?: string | null;
  login_method?: string | null;
  deactivated?: boolean | null;
  // Per-admin module permission set (JSONB array of admin ScreenIds; NULL = the
  // role default). Only meaningful for the reduced `admin` tier; SuperUsers have
  // everything. Read by src/app/access.ts. (docs/superuser-role-plan.md)
  admin_permissions?: unknown;
}

// Normalise the JSONB admin_permissions column into a string[] or null. A stored
// non-array (legacy / malformed) safely degrades to null → the role default.
export function coerceAdminPermissions(value: unknown): string[] | null {
  return Array.isArray(value) ? (value as string[]) : null;
}

export interface SessionUserShelterRow {
  shelter_code: string;
  shelter_id?: string | null;
  is_primary?: boolean;
  // Contact-model Phase 3: the per-service CONTACT phone/mobile on the membership.
  // The new-request phone prefill is sourced from the primary membership's contact
  // (was profiles.phones, dropped in Phase 3).
  phone?: string | null;
  mobile?: string | null;
  // The per-service CONTACT name — the caseworker's own name. The wall: a
  // caseworker's identity lives ONLY here, never on profiles.staff_*.
  first_name?: string | null;
  last_name?: string | null;
}

export interface SessionShelterRow {
  id?: string;
  shelter_name?: string | null;
  state?: string | null;
  postal_line1?: string | null;
  postal_line2?: string | null;
  postal_suburb?: string | null;
  postal_suburb_city?: string | null;
  postal_state?: string | null;
  postal_postcode?: string | null;
  // db-153 portal-only flag: does this DFV Service hold stock? Drives whether the
  // New-Request type chooser is offered (a non-stock service goes straight to Fit out).
  stock_held?: boolean | null;
}

export type DeliveryAddrRow = Record<string, unknown>;

export interface BuildSessionUserParams {
  authUser: SessionAuthUser;
  profile: SessionProfileRow;
  userShelters?: SessionUserShelterRow[] | null;
  shelterRow?: SessionShelterRow | null;
  deliveryAddrRows?: DeliveryAddrRow[] | null;
}

export interface SessionUser {
  id: string;
  profileId: string;
  email: string | undefined;
  role: string | null | undefined;
  displayName: string | undefined;
  // The contact person's name (from profiles.staff_first_name/last_name). Set on BOTH
  // the fresh-login (Login.jsx) and session-restore (buildSessionUser) paths — a new
  // request's Contact Name is built from these, so a gap here put the SERVICE name into
  // Contact Name on any request created after a page refresh.
  firstName: string;
  lastName: string;
  // ⚠️ MISNOMER — this is NOT always "a shelter". It is the name this user is ATTRIBUTED as on
  // requests / comments / tasks. For a SHELTER caseworker it is their DFV-service/org name; for a
  // ZEPHYR STAFF user it is their short public "Display Name" (profiles.preferred_name, e.g.
  // "John W (Zephyr)") so staff full names don't leak to shelters. Both build paths (fresh login +
  // session restore) set it via attributedName() — change that one function, not the call sites.
  shelterName: string | undefined;
  shelterCode: string;
  shelterCodes: string[];
  // The surrogate ids (shelters.id) of every membership — the identity-Phase-2
  // mirror of shelterCodes. The internal key for every lookup; codes stay for
  // display. Used by the multi-shelter chooser (listSheltersByIds).
  shelterIds: string[];
  shelterUuid: string | null;
  state: string;
  shelterAddress: string;
  deliveryAddresses: DeliveryAddrRow[];
  phones: unknown[];
  // Contact-model: the primary membership contact's MOBILE, for the new-request
  // Contact Mobile prefill (separate from `phones`, which carries the phone).
  contactMobile: string;
  // Per-user login method (Google SSO). INERT today — carried onto the user so the
  // Phase 3-5 auth/MFA checks can read it; nothing consumes it yet. Defaults to
  // "password" for every existing user. See src/utils/loginMethod.ts.
  loginMethod: string;
  // Per-admin module permission set (or null → role default). Read by access.ts
  // for the reduced Admin tier; ignored for SuperUsers (they get everything).
  adminPermissions: string[] | null;
  sessionReady: boolean;
  // True when the profile is deactivated. Callers (login + session-restore + the
  // CRM/Feedback mini-apps) must REFUSE such a session — sign out + message — so a
  // deactivated user can't slip in even if the Auth ban never took. The
  // authoritative app-level enforcement of deactivation (docs/account-shelter-
  // lifecycle-plan.md Phase 3); the Auth ban is now defense-in-depth.
  deactivated: boolean;
  // True when a SHELTER-role caseworker has NO user_shelters membership (so no
  // shelter access). Callers (login + restore + CRM/Feedback) must REFUSE such a
  // session loudly. Internal roles (staff/picker/combined/admin) legitimately have
  // no shelter and are NEVER flagged. (docs/retire-profile-shelter-code-plan.md
  // Phase 2 — the single-source-of-truth retirement of profiles.shelter_code.)
  noShelterAccess: boolean;
  // Every user_shelters membership row (code + id + contact phone/mobile + is_primary).
  // Carried so the multi-shelter chooser (PostLoginSelection) can rebuild the FULL
  // active-shelter context — including the SELECTED membership's contact prefill —
  // from resolveActiveShelterContext, rather than the login-derived primary.
  shelterMemberships: SessionUserShelterRow[];
  // db-153 per-active-service flag (rides ActiveShelterContext, so it follows the
  // chosen DFV Service): does this service hold stock? Gates the New-Request type
  // chooser — a non-stock service skips it and goes straight to Fit out.
  stockHeld: boolean;
}

// The full per-shelter session context for ONE active shelter — every field that
// depends on which DFV Service the user is working in. Produced by
// resolveActiveShelterContext so login, session-restore, and the multi-shelter
// chooser all derive an identical, COMPLETE context and none can silently omit a
// field (the cause of the "state stuck on the primary shelter" bug).
export interface ActiveShelterContext {
  shelterCode: string;
  shelterName: string;
  shelterUuid: string | null;
  state: string;
  shelterAddress: string;
  deliveryAddresses: DeliveryAddrRow[];
  phones: unknown[];
  contactMobile: string;
  // The person's own name, from the ACTIVE membership's contact fields.
  firstName: string;
  lastName: string;
  // db-153: does the ACTIVE DFV Service hold stock? Per-service, so it must ride
  // this context (a multi-service caseworker may hold stock in one, not another).
  stockHeld: boolean;
}

// The canonical list of ActiveShelterContext fields. Exported so the drift-guard
// test (sessionUser.test.js) can assert EVERY field reaches the session user. The
// `satisfies` keeps each entry a real key; the exhaustiveness assert just below
// makes the list — and therefore the whole fix — go red at COMPILE time if a field
// is added to the interface but not here. That is the mechanism that ends the
// hand-copied-list drift (docs/session-plan-active-shelter-context-drift.md).
export const ACTIVE_SHELTER_CONTEXT_KEYS = [
  "shelterCode",
  "shelterName",
  "shelterUuid",
  "state",
  "shelterAddress",
  "deliveryAddresses",
  "phones",
  "contactMobile",
  "firstName",
  "lastName",
  "stockHeld",
] as const satisfies readonly (keyof ActiveShelterContext)[];

// Compile-time exhaustiveness: if a field is added to ActiveShelterContext but not
// to ACTIVE_SHELTER_CONTEXT_KEYS above, the conditional resolves to `false` and this
// assignment stops compiling ("true is not assignable to false"). Adding a context
// field can therefore never be done silently — the point of this exercise.
const _activeShelterKeysAreExhaustive: [
  Exclude<keyof ActiveShelterContext, (typeof ACTIVE_SHELTER_CONTEXT_KEYS)[number]>,
] extends [never]
  ? true
  : false = true;
void _activeShelterKeysAreExhaustive;

// The user's primary shelter code = the membership flagged is_primary, else the
// first membership, else null. The single source for the "default" shelter now
// that profiles.shelter_code is retired (docs/retire-profile-shelter-code-plan.md).
export function primaryShelterCode(
  memberships: SessionUserShelterRow[] | null | undefined
): string | null {
  if (!memberships || memberships.length === 0) return null;
  const primary = memberships.find((m) => m.is_primary);
  return (primary ?? memberships[0]).shelter_code ?? null;
}

// The user's primary shelter id = the membership flagged is_primary, else the
// first membership, else null. The identity-Phase-2 mirror of primaryShelterCode
// on shelter_id — the live lookup key for the primary shelter row (getShelterById).
export function primaryShelterId(
  memberships: SessionUserShelterRow[] | null | undefined
): string | null {
  if (!memberships || memberships.length === 0) return null;
  const primary = memberships.find((m) => m.is_primary);
  return (primary ?? memberships[0]).shelter_id ?? null;
}

// The caseworker's CONTACT phone (landline) for the new-request prefill = the
// primary membership's phone ONLY. It no longer falls back to the mobile: the
// new-request form now has a separate Contact Mobile field (prefilled from
// primaryMembershipMobile), so falling back here would duplicate the mobile into
// both boxes for a mobile-only contact. Returns "" when there is no membership /
// no landline.
export function primaryMembershipPhone(
  memberships: SessionUserShelterRow[] | null | undefined
): string {
  if (!memberships || memberships.length === 0) return "";
  const primary = memberships.find((m) => m.is_primary) ?? memberships[0];
  return (primary.phone || "") as string;
}

// The caseworker's OWN name from the primary membership's CONTACT fields — the
// same "primary, else first" rule as the phone/mobile prefills above.
//
// THE WALL (John, 6 Aug 2026): a DFV Service caseworker's identity lives ONLY on
// user_shelters. It is NEVER read from profiles.staff_*, which is Zephyr staff
// only. See docs/staff-caseworker-separation-plan.md.
//
// Used by the SYNCHRONOUS buildSessionUser (session restore), which has no "active
// shelter" concept — a multi-service caseworker re-resolves through
// resolveActiveShelterContext when they pick a service in the chooser, and that
// carries the name for the SELECTED service.
export function primaryMembershipName(memberships: SessionUserShelterRow[] | null | undefined): {
  firstName: string;
  lastName: string;
} {
  if (!memberships || memberships.length === 0) return { firstName: "", lastName: "" };
  const primary = memberships.find((m) => m.is_primary) ?? memberships[0];
  return {
    firstName: (primary.first_name || "") as string,
    lastName: (primary.last_name || "") as string,
  };
}

// The caseworker's CONTACT mobile for the new-request Contact Mobile prefill = the
// primary membership's mobile (only — NOT falling back to phone, so the two fields
// prefill from their own source). Returns "" when there is no membership / no mobile.
export function primaryMembershipMobile(
  memberships: SessionUserShelterRow[] | null | undefined
): string {
  if (!memberships || memberships.length === 0) return "";
  const primary = memberships.find((m) => m.is_primary) ?? memberships[0];
  return (primary.mobile || "") as string;
}

// Join a shelter row's postal fields into the one-line address string used for
// display + the delivery-address fallback. Shared by buildSessionUser and
// resolveActiveShelterContext so the two derivations can't drift.
function buildShelterAddress(shelterRow: SessionShelterRow | null): string {
  if (!shelterRow) return "";
  return [
    shelterRow.postal_line1,
    shelterRow.postal_line2,
    shelterRow.postal_suburb_city,
    shelterRow.postal_state,
    shelterRow.postal_postcode,
  ]
    .filter(Boolean)
    .join(", ");
}

// Build the delivery-address list for a shelter-role user: only the SHOWABLE
// registered addresses, falling back to a synthesised "Main Address" from the
// shelter's postal fields when there are none. The show rule (db-146 promotion
// model, docs/delivery-address-use-count-plan.md) is:
//   use_count >= 3  AND NOT banned_address  AND NOT silent_address  (AND NOT deactivated)
// — deactivated rows are already excluded upstream (listActiveByShelterId). By the
// invariant use_count >= 3 <=> zephyr_verified, an address only reaches the list
// after a responder has verified it 3× (a shelter one-off starts at count 0 and
// is held until promoted). banned = never shown; silent = usable but never listed.
function buildShelterDeliveryAddresses(
  shelterRow: SessionShelterRow | null,
  deliveryAddrRows: DeliveryAddrRow[],
  shelterAddress: string,
  state: string
): DeliveryAddrRow[] {
  const showable = (deliveryAddrRows || []).filter(
    (r) => Number(r.use_count) >= 3 && r.banned_address !== true && r.silent_address !== true
  );
  if (showable.length > 0) return showable;
  if (shelterAddress) {
    return [
      {
        address_name: "Main Address",
        street: shelterRow?.postal_line1 || shelterAddress,
        street2: shelterRow?.postal_line2 || "",
        suburb: shelterRow?.postal_suburb_city || "",
        state: shelterRow?.postal_state || state,
        postcode: shelterRow?.postal_postcode || "",
        is_default: true,
        _fromShelter: true,
      },
    ];
  }
  return [];
}

const EMPTY_ACTIVE_SHELTER: ActiveShelterContext = {
  shelterCode: "",
  shelterName: "",
  shelterUuid: null,
  state: "",
  shelterAddress: "",
  deliveryAddresses: [],
  phones: [],
  contactMobile: "",
  firstName: "",
  lastName: "",
  stockHeld: false,
};

// Resolve the FULL per-shelter session context for the given active shelter. THE
// single source for every shelter-dependent field, used by fresh login, session
// restore, AND the multi-shelter chooser — so the active shelter always drives the
// entire context and no caller can forget a field. Pass shelterId=null (no active
// shelter chosen yet — e.g. a multi-shelter user before the chooser) to get a
// neutral/empty context. `membership` is the SELECTED membership row (its code +
// contact phone/mobile); `isShelterRole` gates the delivery-address fetch.
export async function resolveActiveShelterContext(
  shelterId: string | null,
  membership: SessionUserShelterRow | null,
  isShelterRole: boolean
): Promise<ActiveShelterContext> {
  if (!shelterId) return { ...EMPTY_ACTIVE_SHELTER };

  const shelterRow = (await getShelterById(shelterId)) as SessionShelterRow | null;
  const state = shelterRow?.state || "";
  const shelterAddress = buildShelterAddress(shelterRow);

  let deliveryAddresses: DeliveryAddrRow[] = [];
  if (isShelterRole) {
    const daRows = ((await listActiveByShelterId(shelterRow?.id)) ?? []) as DeliveryAddrRow[];
    deliveryAddresses = buildShelterDeliveryAddresses(shelterRow, daRows, shelterAddress, state);
  }

  const landline = (membership?.phone || "") as string;
  const mobile = (membership?.mobile || "") as string;
  return {
    shelterCode: membership?.shelter_code || "",
    shelterName: shelterRow?.shelter_name || "",
    shelterUuid: shelterRow?.id || shelterId,
    state,
    shelterAddress,
    deliveryAddresses,
    phones: landline ? [{ number: landline }] : [],
    contactMobile: mobile,
    // The caseworker's OWN name comes from the ACTIVE membership's contact — the
    // contact IS the person, per service (the wall: a caseworker's identity lives
    // only on user_shelters, never on profiles.staff_*). Riding on the active
    // context matters for a multi-service caseworker: the name travels with the
    // service they chose, exactly like state / address / phone already do.
    firstName: (membership?.first_name || "") as string,
    lastName: (membership?.last_name || "") as string,
    // db-153: per-active-service stock flag — drives the New-Request type chooser.
    stockHeld: shelterRow?.stock_held === true,
  };
}

// Apply a resolved ActiveShelterContext onto a session user in ONE shot. This is the
// ONLY place a context is copied onto a session user — Login.jsx and
// PostLoginSelection.jsx both go through it — so a field added to ActiveShelterContext
// cannot be silently forgotten by one call site. TWO bugs came from hand-maintained
// copy lists that fell behind the type: the 1 Aug "state stuck on the primary shelter"
// and the 6 Aug "Contact Name shows the DFV Service name" (firstName/lastName missing
// from the PostLoginSelection list). See docs/session-plan-active-shelter-context-drift.md.
export function applyActiveShelterContext<T extends object>(
  user: T,
  ctx: ActiveShelterContext
): T & ActiveShelterContext {
  return Object.assign(user, ctx);
}

// The name to record as a request's contact_name: the caseworker's OWN name (the
// session firstName/lastName) if present, else the profile display name. Shared by
// the submit builders (RequestWizard / RequestForm) AND the draft-save path
// (useRequestPersistence) so a DRAFT records the SAME contact name a submit would.
// Drafts used to save it blank: the wizard's four draft-save objects omit the field,
// and only the submit builder set it, so a draft's read view showed no Contact Name
// until submit. The wizard's on-SCREEN label keeps its own DFV-Service-name fallback
// + guidance caption; this is the STORED value. Returns "" when no name is known.
// `unknown` param, narrowed inside: the callers pass differently-shaped user objects
// (the session user declares these fields; the persistence layer's AppUser does not),
// and a named all-optional param would trip TS's weak-type check on the latter.
export function contactNameForUser(user: unknown): string {
  const u = (user ?? {}) as {
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
  };
  if (u.firstName || u.lastName) {
    return [u.firstName, u.lastName].filter(Boolean).join(" ");
  }
  return u.displayName || "";
}

// The name a user is ATTRIBUTED as on requests / comments / tasks — i.e. the confusingly-named
// `SessionUser.shelterName`. ONE source of truth for BOTH build paths (fresh login in Login.jsx +
// session restore in buildSessionUser) so they can't drift:
//   • a SHELTER caseworker → their DFV-service/org name (passed in as shelterOrgName);
//   • a ZEPHYR STAFF user → their short public "Display Name" (profiles.preferred_name), falling
//     back to a computed "<first> <last-initial> (Zephyr)", then display_name — so a full staff
//     name can never leak to a shelter.
// A shelter caseworker is attributed by their org name and NEVER reaches the staff branch.
export function attributedName(
  shelterOrgName: string | null | undefined,
  profile: Pick<
    SessionProfileRow,
    "role" | "preferred_name" | "staff_first_name" | "staff_last_name" | "display_name"
  >,
  emailFallback?: string | null
): string {
  if (shelterOrgName) return shelterOrgName; // shelter caseworker → org name
  if (profile.role !== ROLES.SHELTER) {
    // Zephyr staff (or any internal role): short public name, never the full name.
    const pref = (profile.preferred_name || "").trim();
    if (pref) return pref;
    const first = (profile.staff_first_name || "").trim();
    if (first) {
      const initial = (profile.staff_last_name || "").trim().charAt(0);
      return initial ? `${first} ${initial} (Zephyr)` : `${first} (Zephyr)`;
    }
  }
  return profile.display_name || emailFallback || "";
}

export function buildSessionUser(params: BuildSessionUserParams): SessionUser {
  const authUser = params.authUser;
  const profile = params.profile;
  const userShelters =
    params.userShelters && params.userShelters.length > 0 ? params.userShelters : [];
  const shelterRow = params.shelterRow || null;
  const deliveryAddrRows =
    params.deliveryAddrRows && params.deliveryAddrRows.length > 0 ? params.deliveryAddrRows : [];

  // Single source of truth: shelter membership comes ONLY from user_shelters. The
  // legacy profiles.shelter_code fallback is RETIRED here (docs/retire-profile-
  // shelter-code-plan.md Phase 2); primaryShelterCode picks the default shelter.
  const shelterCodes = userShelters.map((s) => s.shelter_code);
  const shelterIds = userShelters
    .map((s) => s.shelter_id)
    .filter((id): id is string => Boolean(id));
  const primaryCode = primaryShelterCode(userShelters);
  // A shelter caseworker with no membership has no shelter access - flag it so the
  // session-build callers refuse loudly. Internal roles (staff/picker/combined/
  // admin) legitimately have no shelter and are NEVER flagged.
  const noShelterAccess = profile.role === ROLES.SHELTER && shelterCodes.length === 0;

  // See attributedName(): shelter caseworker → org name; Zephyr staff → short "Display Name".
  const resolvedShelterName = attributedName(shelterRow?.shelter_name, profile, authUser.email);
  const resolvedState = shelterRow?.state || "";
  const resolvedShelterAddress = buildShelterAddress(shelterRow);

  const u: SessionUser = {
    id: authUser.id,
    profileId: profile.id,
    email: authUser.email,
    role: profile.role,
    displayName: profile.display_name || authUser.email,
    // The person's own name, from the primary membership's CONTACT fields — NOT
    // profiles.staff_*, which is Zephyr staff only (the wall; 6 Aug 2026). Set here
    // as well as on fresh login so a restored session yields the person's name, not
    // the service name, in Contact Name.
    firstName: primaryMembershipName(userShelters).firstName,
    lastName: primaryMembershipName(userShelters).lastName,
    shelterName: resolvedShelterName,
    shelterCode: primaryCode || "",
    shelterCodes: shelterCodes,
    shelterIds: shelterIds,
    // Surrogate FK to shelters.id -- used as shelter_id on new requests. Must be
    // populated on BOTH the session-restore and fresh-login paths; missing it
    // caused shelter_id: null on every request submitted after a page refresh.
    shelterUuid: shelterRow?.id || null,
    state: resolvedState,
    shelterAddress: resolvedShelterAddress,
    deliveryAddresses: [],
    // Contact-model Phase 3 (Stage 1 de-wire): the new-request phone prefill is now
    // sourced from the primary membership's CONTACT phone/mobile, NOT profiles.phones
    // (dropped in Stage 2). Kept in the {number} shape the form prefill reads.
    phones: primaryMembershipPhone(userShelters)
      ? [{ number: primaryMembershipPhone(userShelters) }]
      : [],
    contactMobile: primaryMembershipMobile(userShelters),
    loginMethod: normaliseLoginMethod(profile.login_method),
    adminPermissions: coerceAdminPermissions(profile.admin_permissions),
    sessionReady: !(profile.role === ROLES.COMBINED || shelterCodes.length > 1),
    deactivated: !!profile.deactivated,
    noShelterAccess: noShelterAccess,
    shelterMemberships: userShelters,
    // db-153: primary shelter's stock flag (the multi-shelter chooser later overrides
    // this with the ACTIVE service's value via applyActiveShelterContext, like state/
    // address/shelterUuid). Gates the New-Request type chooser.
    stockHeld: shelterRow?.stock_held === true,
  };

  if (profile.role === ROLES.SHELTER) {
    u.deliveryAddresses = buildShelterDeliveryAddresses(
      shelterRow,
      deliveryAddrRows,
      resolvedShelterAddress,
      resolvedState
    );
  }

  return u;
}

// Default landing page for a user, from their role and (post-selection)
// sessionMode. Consolidates the equivalent logic that previously lived inline at
// three portal.jsx call sites (session-restore, login, PostLoginSelection).
// Verified behaviour-preserving for every reachable input: sessionMode is only
// ever set (to "responder"/"picker") for combined users via PostLoginSelection,
// so the role-vs-mode edge cases that would diverge can't occur in practice.
export interface PageRoleUser {
  role?: string | null;
  sessionMode?: string | null;
}

export function defaultPageForUser(user: PageRoleUser): "todo" | "pickers" | "dashboard" {
  // Delegates to the single source of truth (src/app/access.ts). Kept as a named
  // export so existing import sites (portal.jsx, useAuthSession) are unchanged.
  return defaultScreenFor(user);
}

// Fetch the rows needed to build the session user (profiles + user_shelters, then
// shelters and shelter delivery_addresses as applicable) and assemble them via
// buildSessionUser. Returns null if the profile can't be loaded -- the caller
// then just stops restoring the session. Pulled out of portal.jsx's
// session-restore effect so the fetch orchestration is unit-testable; the React
// effect / onAuthStateChange wiring stays in portal.jsx.
export async function loadSessionUser(authUser: SessionAuthUser): Promise<SessionUser | null> {
  // Profile + user_shelters in parallel.
  const [profileRes, sheltersRes] = await Promise.all([
    getProfileByIdResult(authUser.id),
    listUserSheltersByUserId(authUser.id),
  ]);
  if (profileRes.error) {
    logger.warn("Profile fetch failed:", profileRes.error.message);
    return null;
  }
  const profile = profileRes.data;
  if (!profile) return null;

  const userShelters = sheltersRes && sheltersRes.length > 0 ? sheltersRes : [];

  // A MULTI-shelter user always re-runs the chooser (sessionReady=false forces
  // PostLoginSelection on both login AND restore), which rebuilds the full active-
  // shelter context via resolveActiveShelterContext. So don't derive any shelter-
  // specific context here for them — it would only be discarded, and deriving a
  // "primary" state that then leaks unreplaced was the QLD-stuck bug. Derive only
  // when there's exactly one (or no) membership, i.e. nothing to choose.
  const activeId = userShelters.length > 1 ? null : primaryShelterId(userShelters);

  // shelter_name / state / address come from the shelters table, keyed by the
  // active membership id. Repo owns the query (+ error logging); see data/shelters.ts.
  let shelterRow: SessionShelterRow | null = null;
  if (activeId) {
    shelterRow = await getShelterById(activeId);
  }

  // Shelter-user delivery addresses, keyed by the resolved shelter's id.
  let deliveryAddrRows: DeliveryAddrRow[] = [];
  if (profile.role === ROLES.SHELTER && activeId) {
    const daRows = await listActiveByShelterId(shelterRow?.id);
    deliveryAddrRows = (daRows ?? []) as DeliveryAddrRow[];
  }

  // The repo returns the generated ProfileRow; buildSessionUser takes the local
  // (decoupled, minimal) SessionProfileRow. The only incompatibility is `phones`
  // (generated Json|null vs the interface's unknown[]|null), so bridge with the
  // sanctioned as-unknown-as cast rather than coupling the tested pure fn to the
  // schema type.
  const user = buildSessionUser({
    authUser,
    profile: profile as unknown as SessionProfileRow,
    userShelters,
    shelterRow,
    deliveryAddrRows,
  });

  // A shelter caseworker with no membership has no shelter access - log it loudly
  // (data gap) so it's visible in the audit log; the session-build callers refuse
  // on user.noShelterAccess. Internal roles (staff/picker/combined/admin) are never
  // flagged. (docs/retire-profile-shelter-code-plan.md Phase 2)
  if (user.noShelterAccess) {
    logger.warn(
      `[sessionUser] shelter profile ${profile.id} has no user_shelters membership - session refused (no shelter access).`
    );
    logAudit("session_refused_no_shelter", { profile_id: profile.id, via: "restore" });
  }

  return user;
}
